"use client"

import { PortableTextBlock } from "next-sanity";
import Image from "next/image";
import useEmblaCarousel from 'embla-carousel-react';
import type { EmblaCarouselType } from 'embla-carousel';
import AutoScroll from 'embla-carousel-auto-scroll';
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { HomepageQueryResult, PageQueryResult } from "@/sanity.types";
import CustomPortableText from "@/components/portable-text";
import ArrowRight from "@/components/ArrowRight";
import useHasMounted from '@/hooks/useHasMounted';
import useMediaQuery from '@/hooks/useMediaQuery';
import { getOrderedImageTextCarouselBlocks } from "@/lib/marketing-image-overrides";

type ImageTextCarouselProps = Extract<NonNullable<NonNullable<HomepageQueryResult>["content"]>[number], { _type: "imageTextCarousel" }> | Extract<NonNullable<NonNullable<PageQueryResult>["content"]>[number], { _type: "imageTextCarousel" }>;
type ImageTextBlocksProps = NonNullable<ImageTextCarouselProps["imageTextBlocks"]>
type CarouselBlock = NonNullable<ImageTextBlocksProps>[number];

/** Pixels advanced per animation frame — lower is slower. */
const AUTO_SCROLL_SPEED = 0.25;

const carouselArrowClassName =
  'flex size-10 cursor-pointer items-center justify-center rounded-full bg-(--color-background) text-(--color-foreground) transition-colors hover:bg-[color-mix(in_srgb,var(--color-tangerine)_20%,var(--color-background))] active:bg-[color-mix(in_srgb,var(--color-tangerine)_20%,var(--color-background))]';

/** Images are rendered separately as the slide photo, so drop them from the text content. */
function filterCarouselContent(content?: PortableTextBlock[] | null) {
  if (!content) return content;
  return content.filter((block) => block._type !== 'image');
}

type CarouselContentImage = {
  _type: 'image';
  alt?: string | null;
  assetPath?: string | null;
};

type CarouselPhoto = {
  assetPath: string;
  alt: string;
};

function getCarouselContentPhotos(content?: PortableTextBlock[] | null): CarouselPhoto[] {
  if (!content) return [];

  return (content as CarouselContentImage[])
    .filter((entry): entry is CarouselContentImage & { assetPath: string } =>
      entry._type === 'image' && Boolean(entry.assetPath),
    )
    .map((entry) => ({
      assetPath: entry.assetPath,
      alt: entry.alt ?? '',
    }));
}

function getBlockPhotos(block: CarouselBlock) {
  const { content, image } = block;
  const contentPhotos = getCarouselContentPhotos(content as PortableTextBlock[] | undefined);
  const blockPhoto = image?.assetPath
    ? { assetPath: image.assetPath, alt: image.caption ?? '' }
    : null;

  return {
    primaryPhoto: contentPhotos[0] ?? blockPhoto,
    hoverPhoto: contentPhotos[1] ?? null,
  };
}

function CarouselSlidePhotos({
  primaryPhoto,
  hoverPhoto,
  priority,
  enableTapToggle = false,
}: {
  primaryPhoto: CarouselPhoto | null;
  hoverPhoto: CarouselPhoto | null;
  priority?: boolean;
  enableTapToggle?: boolean;
}) {
  const canToggle = enableTapToggle && Boolean(hoverPhoto);

  // Tap mode opens on the childhood photo as a hint that the photo is
  // interactive: once the photo is fully on screen it lingers for 500ms, then
  // fades to the default photo (see the preview effect below).
  const [showAltPhoto, setShowAltPhoto] = useState(canToggle);
  const pointerStart = useRef<{ x: number; y: number } | null>(null);
  const lastPointerToggleAt = useRef(0);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const previewTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previewDoneRef = useRef(false);

  useEffect(() => {
    if (!canToggle) return;
    const node = buttonRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting || previewDoneRef.current) return;
      previewDoneRef.current = true;
      observer.disconnect();
      previewTimerRef.current = setTimeout(() => setShowAltPhoto(false), 500);
    }, { threshold: 0.9 });

    observer.observe(node);

    return () => {
      observer.disconnect();
      if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    };
  }, [canToggle]);

  if (!primaryPhoto) return null;

  const togglePhoto = () => {
    // A tap takes over from the intro preview: stop its pending fade so it
    // can't overwrite what the user chose.
    previewDoneRef.current = true;
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    setShowAltPhoto((current) => !current);
  };
  // Hover-swap and tap-toggle must be mutually exclusive: mobile browsers
  // simulate a "sticky" hover on tap, which would override the tap state.
  const swapOnHover = Boolean(hoverPhoto) && !canToggle;

  // Tap mode fades a touch slower (800ms) so the intro preview reads as
  // intentional; desktop hover keeps the snappier 500ms.
  const fadeClassName = canToggle
    ? 'transition-opacity duration-[800ms]'
    : 'transition-opacity duration-500';

  const photoStack = (
    <>
      <Image
        src={primaryPhoto.assetPath}
        alt={primaryPhoto.alt}
        fill
        sizes="18rem"
        className={`object-cover ${fadeClassName} ease-in-out motion-reduce:transition-none${swapOnHover ? ' group-hover:opacity-0' : ''}${showAltPhoto ? ' opacity-0' : ''}`}
        draggable={false}
        loading={priority ? 'eager' : 'lazy'}
      />
      {hoverPhoto &&
        <Image
          src={hoverPhoto.assetPath}
          alt={hoverPhoto.alt}
          fill
          sizes="18rem"
          aria-hidden="true"
          className={`object-cover ${fadeClassName} ease-in-out motion-reduce:transition-none${swapOnHover ? ' group-hover:opacity-100' : ''}${showAltPhoto ? ' opacity-100' : ' opacity-0'}`}
          draggable={false}
          loading={canToggle && priority ? 'eager' : 'lazy'}
        />
      }
    </>
  );

  const wrapperClassName = `relative mb-7.5 block aspect-square w-full transform-gpu${canToggle ? ' cursor-pointer touch-manipulation border-0 bg-transparent p-0 text-left' : ''}`;

  if (canToggle) {
    return (
      <button
        ref={buttonRef}
        type="button"
        className={wrapperClassName}
        // iOS inside a scroll container doesn't reliably synthesize click from a
        // tap, so detect the tap from raw pointer events instead. A tap = finger
        // down and up in (nearly) the same spot; larger movement means a scroll.
        onPointerDown={(event) => {
          pointerStart.current = { x: event.clientX, y: event.clientY };
        }}
        onPointerUp={(event) => {
          const start = pointerStart.current;
          pointerStart.current = null;
          if (!start) return;
          const moved = Math.hypot(event.clientX - start.x, event.clientY - start.y);
          if (moved < 12) {
            lastPointerToggleAt.current = Date.now();
            togglePhoto();
          }
        }}
        onPointerCancel={() => {
          pointerStart.current = null;
        }}
        // Browsers follow a handled tap with a synthesized click on the same
        // button. Toggling on both would flip the photo twice (looks like
        // nothing happened), so only toggle here when no pointer tap was just
        // handled — this path then covers keyboard activation (Enter/Space).
        onClick={() => {
          if (Date.now() - lastPointerToggleAt.current < 700) return;
          togglePhoto();
        }}
        aria-pressed={showAltPhoto}
        aria-label={showAltPhoto ? 'Show primary photo' : 'Show alternate photo'}
      >
        {photoStack}
      </button>
    );
  }

  return (
    <div className={wrapperClassName}>
      {photoStack}
    </div>
  );
}

function CarouselSlide({
  block,
  index,
  slideCount,
  enableTapPhotoToggle = false,
}: {
  block: CarouselBlock;
  index: number;
  slideCount: number;
  enableTapPhotoToggle?: boolean;
}) {
  const filteredContent = filterCarouselContent(block.content as PortableTextBlock[] | undefined);
  const { primaryPhoto, hoverPhoto } = getBlockPhotos(block);

  return (
    <article
      className="group kids-carousel-slide"
      aria-roledescription="slide"
      aria-label={`Slide ${index + 1} of ${slideCount}`}
    >
      <CarouselSlidePhotos
        primaryPhoto={primaryPhoto}
        hoverPhoto={hoverPhoto}
        priority={index === 0}
        enableTapToggle={enableTapPhotoToggle}
      />
      {filteredContent && filteredContent.length > 0 &&
        <div className="rte w-full">
          <CustomPortableText useSmallBody value={filteredContent as PortableTextBlock[]} />
        </div>
      }
    </article>
  );
}

const carouselArrowRowClassName =
  'marketing-container flex items-center justify-between px-5 pb-5 md:px-7.5 md:pb-7.5';

function isLastSlideAtRightEdge(emblaApi: EmblaCarouselType): boolean {
  const slides = emblaApi.slideNodes();
  if (slides.length === 0) return false;

  const lastSlide = slides[slides.length - 1];
  const viewport = emblaApi.rootNode();
  const slideRect = lastSlide.getBoundingClientRect();
  const viewportRect = viewport.getBoundingClientRect();
  const tolerance = 2;

  return (
    slideRect.left >= viewportRect.left - tolerance &&
    slideRect.right <= viewportRect.right + tolerance &&
    slideRect.right >= viewportRect.right - tolerance
  );
}

function CarouselArrowSpacer() {
  return (
    <div className={carouselArrowRowClassName} aria-hidden="true">
      <div className="size-10 shrink-0" />
      <div className="size-10 shrink-0" />
    </div>
  );
}

/** Mobile: native overflow scroll — no Embla, reliable iOS swipe. */
function KidsCarouselMobile({ blocks }: { blocks: CarouselBlock[] }) {
  return (
    <div className="kids-carousel">
      <CarouselArrowSpacer />
      <div
        className="kids-carousel-scroll px-5 pb-5"
        role="region"
        aria-roledescription="carousel"
        aria-label="Team members"
      >
      <div className="kids-carousel-scroll__track">
        {blocks.map((block, i) => (
          <CarouselSlide
            key={i}
            block={block}
            index={i}
            slideCount={blocks.length}
            enableTapPhotoToggle
          />
        ))}
      </div>
    </div>
    </div>
  );
}

/** Desktop: Embla with auto-scroll animation. */
function KidsCarouselDesktop({ blocks }: { blocks: CarouselBlock[] }) {
  const carouselRootRef = useRef<HTMLDivElement>(null);
  const hasReachedEndRef = useRef(false);

  const autoScrollRootNode = useCallback(() => carouselRootRef.current, []);

  const carouselPlugins = useMemo(() => [
    AutoScroll({
      direction: 'forward',
      speed: AUTO_SCROLL_SPEED,
      startDelay: 0,
      playOnInit: false,
      stopOnMouseEnter: true,
      stopOnInteraction: false,
      stopOnFocusIn: false,
      rootNode: autoScrollRootNode,
    }),
  ], [autoScrollRootNode]);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    loop: false,
    skipSnaps: false,
    // Clamp scrolling so the user can't drag past "last card at the right edge".
    containScroll: 'trimSnaps',
  }, carouselPlugins);

  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    if (!emblaApi) return;
    hasReachedEndRef.current = isLastSlideAtRightEdge(emblaApi);
    emblaApi.reInit();
  }, [emblaApi, blocks.length]);

  const stopAutoScrollAtEnd = useCallback((embla: EmblaCarouselType) => {
    if (hasReachedEndRef.current) return;
    if (!isLastSlideAtRightEdge(embla)) return;

    hasReachedEndRef.current = true;
    embla.plugins()?.autoScroll?.stop();
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    const onScroll = () => stopAutoScrollAtEnd(emblaApi);

    emblaApi.on('scroll', onScroll);
    onScroll();

    return () => {
      emblaApi.off('scroll', onScroll);
    };
  }, [emblaApi, stopAutoScrollAtEnd]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const syncPreference = () => setPrefersReducedMotion(mediaQuery.matches);
    syncPreference();
    mediaQuery.addEventListener('change', syncPreference);
    return () => mediaQuery.removeEventListener('change', syncPreference);
  }, []);

  useEffect(() => {
    if (!emblaApi || prefersReducedMotion) return;

    const root = carouselRootRef.current;
    if (!root) return;

    const autoScroll = emblaApi.plugins()?.autoScroll;
    if (!autoScroll) return;

    const syncPlayback = (isVisible: boolean) => {
      if (hasReachedEndRef.current) {
        autoScroll.stop();
        return;
      }

      if (isVisible) {
        autoScroll.play();
      } else {
        autoScroll.stop();
      }
    };

    const observer = new IntersectionObserver(
      ([entry]) => syncPlayback(entry.isIntersecting),
      { threshold: 0.25 },
    );

    observer.observe(root);

    const blockRestartAfterEnd = () => {
      if (hasReachedEndRef.current) autoScroll.stop();
    };

    emblaApi.on('autoScroll:play', blockRestartAfterEnd);

    return () => {
      observer.disconnect();
      emblaApi.off('autoScroll:play', blockRestartAfterEnd);
      autoScroll.stop();
    };
  }, [emblaApi, prefersReducedMotion]);

  useEffect(() => {
    if (!emblaApi) return;

    const root = emblaApi.rootNode();
    const onWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;

      event.preventDefault();
      if (event.deltaX > 0) emblaApi.scrollNext();
      else emblaApi.scrollPrev();
    };

    root.addEventListener('wheel', onWheel, { passive: false });
    return () => root.removeEventListener('wheel', onWheel);
  }, [emblaApi]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  const onKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      scrollPrev();
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      scrollNext();
    }
  }, [scrollNext, scrollPrev]);

  return (
    <div ref={carouselRootRef} className="kids-carousel kids-carousel--desktop relative">
      <div className={carouselArrowRowClassName}>
        <button
          type="button"
          className={carouselArrowClassName}
          onClick={scrollPrev}
          aria-label="Previous slide"
        >
          <ArrowRight className="size-5 -scale-x-100" />
        </button>
        <button
          type="button"
          className={carouselArrowClassName}
          onClick={scrollNext}
          aria-label="Next slide"
        >
          <ArrowRight className="size-5" />
        </button>
      </div>

      <div
        className="embla__viewport overflow-hidden px-7.5 pb-7.5"
        ref={emblaRef}
        onKeyDown={onKeyDown}
        role="region"
        aria-roledescription="carousel"
        aria-label="Team members"
      >
        <div className="embla__container flex">
          {blocks.map((block, i) => (
            <div key={i} className="embla__slide">
              <CarouselSlide block={block} index={i} slideCount={blocks.length} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function EmblaCarousel ({
  carouselKey,
  imageTextBlocks,
}: {
  carouselKey?: string;
  imageTextBlocks: ImageTextBlocksProps;
}) {
  const blocks = getOrderedImageTextCarouselBlocks(carouselKey, imageTextBlocks) ?? imageTextBlocks;
  const hasMounted = useHasMounted();
  const isMdUp = useMediaQuery('(min-width: 768px)');

  // SSR and the first client paint always use mobile markup so they match.
  if (!hasMounted || !isMdUp) {
    return <KidsCarouselMobile blocks={blocks} />;
  }

  return <KidsCarouselDesktop blocks={blocks} />;
}
